import React, { useState, useEffect } from 'react';
import { ArrowRight, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Counter = ({ end, duration = 2000, suffix = '' }) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let startTime;
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      setCount(Math.floor(progress * end));
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    requestAnimationFrame(animate);
  }, [end, duration]);

  return <span>{count}{suffix}</span>;
};

const Home = () => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '' });
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchFaqs();
  }, []);

  const fetchFaqs = async () => {
    try {
      const response = await axios.get(`${API}/faqs`);
      setFaqs(response.data);
    } catch (error) {
      console.error('Error fetching FAQs:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, formData);
      toast({
        title: "Message Sent Successfully!",
        description: "Our team will assist you promptly.",
      });
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to send message. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const cryptoData = [
    { 
      symbol: 'BTC', 
      name: 'Bitcoin', 
      color: 'from-orange-400 to-orange-600',
      logo: 'https://s2.coinmarketcap.com/static/img/coins/64x64/1.png'
    },
    { 
      symbol: 'ETH', 
      name: 'Ethereum', 
      color: 'from-indigo-400 to-purple-600',
      logo: 'https://s2.coinmarketcap.com/static/img/coins/64x64/1027.png'
    },
    { 
      symbol: 'BCH', 
      name: 'Bitcoin Cash', 
      color: 'from-green-500 to-emerald-600',
      logo: 'https://s2.coinmarketcap.com/static/img/coins/64x64/1831.png'
    },
    { 
      symbol: 'USDC', 
      name: 'USD Coin', 
      color: 'from-blue-400 to-blue-600',
      logo: 'https://s2.coinmarketcap.com/static/img/coins/64x64/3408.png'
    },
    { 
      symbol: 'BNB', 
      name: 'BNB', 
      color: 'from-yellow-400 to-orange-500',
      logo: 'https://s2.coinmarketcap.com/static/img/coins/64x64/1839.png'
    }
  ];

  const fiats = ['EUR'];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center px-4 md:px-6 pt-16 pb-4 md:pb-6">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-orange-50/30 via-white to-amber-50/20"></div>
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(255,107,53,0.06),transparent_60%),radial-gradient(circle_at_70%_80%,rgba(247,147,30,0.06),transparent_60%)]"></div>
        </div>
        
        <div className="relative z-10 max-w-6xl mx-auto text-center">
          <div className="space-y-8 md:space-y-10">
            <h1 className="text-5xl md:text-7xl font-bold tracking-tight text-[#1E3A5F] leading-tight">
              Secure{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF6B35] via-[#F7931E] to-[#FFA726]">
                Crypto-to-Fiat
              </span>{' '}
              OTC Exchange for Verified Clients
            </h1>
            <p className="text-lg md:text-xl text-[#5B6B7F] max-w-3xl mx-auto leading-relaxed px-2">
              We provide regulated and secure over-the-counter digital-asset exchange services, enabling verified individual and corporate clients to buy and sell major cryptocurrencies with full compliance and banking support. Services are available to verified clients only and subject to AML/KYC approval. We do not offer retail trading or custody.
            </p>
          </div>
          
          <div className="mt-16 md:mt-24">
            <a href="/contact">
              <Button 
                size="lg" 
                className="bg-gradient-to-r from-[#FF6B35] to-[#F7931E] hover:from-[#F7931E] hover:to-[#FF6B35] text-white px-10 md:px-12 py-5 md:py-6 text-base md:text-lg font-semibold rounded-2xl transition-all duration-200 shadow-[0_4px_12px_rgba(0,0,0,0.06)] hover:shadow-[0_6px_16px_rgba(0,0,0,0.08)]"
              >
                Get Started <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Crypto Display Section - Clean Grid Layout */}
      <section className="py-8 md:py-10 px-4 md:px-6 bg-gradient-to-b from-[#FAFBFC] via-white to-[#FAFBFC]">
        <div className="max-w-6xl mx-auto">
          <div className="space-y-10 md:space-y-16">
            {/* Crypto Icons Grid - Organized and Clean */}
            <div className="flex flex-wrap justify-center items-center gap-4 md:gap-8">
              {cryptoData.map((crypto, index) => (
                <div 
                  key={index}
                  className="group relative"
                >
                  {/* Glow Effect on Hover */}
                  <div className="absolute inset-0 bg-gradient-to-br from-[#FF6B35]/20 to-[#F7931E]/20 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-all duration-300"></div>
                  
                  {/* Icon Container */}
                  <div className="relative bg-white rounded-2xl p-3 md:p-4 shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-110 w-24 h-24 md:w-32 md:h-32 flex flex-col items-center justify-center border-2 border-gray-100 group-hover:border-[#FF6B35]/30">
                    {/* Crypto Logo */}
                    <img 
                      src={crypto.logo}
                      alt={crypto.name}
                      className="w-12 h-12 md:w-16 md:h-16 object-contain mb-1 md:mb-2"
                      crossOrigin="anonymous"
                    />
                    {/* Symbol Label */}
                    <span className="text-xs md:text-sm font-bold text-[#1E3A5F]">{crypto.symbol}</span>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Fiat Currencies - Clean Display */}
            <div className="flex flex-wrap justify-center gap-2 md:gap-3">
              {fiats.map((fiat) => (
                <div key={fiat} className="px-4 py-2 md:px-6 md:py-3 bg-white border-2 border-gray-200 rounded-lg md:rounded-xl font-bold text-xs md:text-sm text-[#1E3A5F] hover:border-[#FF6B35] hover:shadow-lg hover:scale-105 transition-all shadow-sm">
                  {fiat}
                </div>
              ))}
            </div>
            
            {/* Asset availability note */}
            <div className="text-center mt-6">
              <p className="text-xs md:text-sm text-[#5B6B7F] italic">
                Asset availability is subject to liquidity, jurisdiction, and compliance approval.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Simple Service Info */}
      <section className="py-14 md:py-28 px-4 md:px-6">
        <div className="max-w-6xl mx-auto text-center space-y-8 md:space-y-12">
          <h2 className="text-3xl md:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#FF6B35] to-[#F7931E]">
            Our service
          </h2>
          
          <div className="grid md:grid-cols-3 gap-4 md:gap-6 text-left">
            <Card className="border border-gray-200 hover:border-[#FF6B35]/50 transition-all duration-300 hover:shadow-xl rounded-xl bg-white group">
              <CardContent className="p-5 md:p-6">
                <div className="flex items-start gap-3 mb-3">
                  <CheckCircle className="h-5 w-5 md:h-6 md:w-6 text-[#FF6B35] flex-shrink-0 mt-1" />
                  <p className="text-sm md:text-base text-[#5B6B7F] leading-relaxed group-hover:text-[#1E3A5F] transition-colors">
                    We are a regulated cryptocurrency exchange service provider and work exclusively with licensed cryptocurrency OTC trading desks and compliant banking partners.
                  </p>
                </div>
              </CardContent>
            </Card>
            
            <Card className="border border-gray-200 hover:border-[#FF6B35]/50 transition-all duration-300 hover:shadow-xl rounded-xl bg-white group">
              <CardContent className="p-5 md:p-6">
                <div className="flex items-start gap-3 mb-3">
                  <CheckCircle className="h-5 w-5 md:h-6 md:w-6 text-[#FF6B35] flex-shrink-0 mt-1" />
                  <p className="text-sm md:text-base text-[#5B6B7F] leading-relaxed group-hover:text-[#1E3A5F] transition-colors">
                    We provide quick, professional, and reliable OTC crypto-fiat and fiat-crypto exchange services for verified individuals and corporate clients.
                  </p>
                </div>
              </CardContent>
            </Card>
            
            <Card className="border border-gray-200 hover:border-[#FF6B35]/50 transition-all duration-300 hover:shadow-xl rounded-xl bg-white group">
              <CardContent className="p-5 md:p-6">
                <div className="flex items-start gap-3 mb-3">
                  <CheckCircle className="h-5 w-5 md:h-6 md:w-6 text-[#FF6B35] flex-shrink-0 mt-1" />
                  <p className="text-sm md:text-base text-[#5B6B7F] leading-relaxed group-hover:text-[#1E3A5F] transition-colors">
                    We can exchange major assets including BTC, ETH, BCH, USDC, and BNB. All transactions are processed via secure compliance-verified banking channels and subject to AML/KYC procedures.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
          
          {/* Compliance disclaimer */}
          <div className="text-center mt-8">
            <p className="text-xs text-gray-400">
              We operate under strict AML/KYC and regulatory standards to ensure secure and compliant transactions.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-14 md:py-28 px-4 md:px-6 bg-gradient-to-b from-[#FAFBFC] to-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-3xl md:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#FF6B35] to-[#F7931E] mb-3 md:mb-4">
              FAQ
            </h2>
            <p className="text-base md:text-xl text-[#5B6B7F] px-4">
              Find answers to common questions about our services
            </p>
          </div>
          
          {faqs.length > 0 ? (
            <Accordion type="single" collapsible className="space-y-3 md:space-y-4">
              {faqs.map((faq) => (
                <AccordionItem 
                  key={faq._id || faq.id} 
                  value={faq._id || faq.id}
                  className="bg-white border-2 border-gray-200 rounded-xl px-4 md:px-6 hover:border-[#FF6B35]/50 transition-all shadow-sm hover:shadow-lg"
                >
                  <AccordionTrigger className="text-left text-base md:text-lg font-semibold text-[#1E3A5F] hover:text-[#FF6B35] transition-colors py-4 md:py-5">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm md:text-base text-[#5B6B7F] leading-relaxed pb-4 md:pb-5 whitespace-pre-line">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          ) : (
            <div className="text-center py-12">
              <p className="text-[#5B6B7F]">Loading FAQs...</p>
            </div>
          )}
        </div>
      </section>

      {/* Simple CTA */}
      <section className="py-14 md:py-28 px-4 md:px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#FF6B35] to-[#F7931E]">
            Ready to get started?
          </h2>
          <p className="text-base md:text-xl text-[#5B6B7F] px-4 mt-6 md:mt-8">
            Submit your service request to begin the onboarding process. Once we receive your information, our compliance team will review your details and contact you to guide you through the next steps for OTC crypto-fiat services.<br /><br />After submitting your request, our team will review your information and contact you within one business day.
          </p>
          <div className="mt-12 md:mt-16">
            <a href="/contact">
              <Button 
                size="lg" 
                className="bg-gradient-to-r from-[#F7931E] to-[#FFA726] hover:from-[#FF6B35] hover:to-[#F7931E] text-white px-8 md:px-10 py-5 md:py-6 text-base md:text-lg font-semibold rounded-xl transition-all duration-300 hover:scale-105 shadow-xl hover:shadow-2xl"
              >
                Get Started <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 md:py-36 px-4 md:px-6 bg-gradient-to-b from-[#FAFBFC] to-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center space-y-4 md:space-y-5 mb-10 md:mb-14">
            <h2 className="text-3xl md:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#FF6B35] to-[#F7931E]">
              Trade or general enquiries
            </h2>
            <p className="text-base md:text-xl text-[#5B6B7F] max-w-3xl mx-auto">
              We're also here to help with any questions you might have on digital-asset transactions, onboarding, or compliance in this fast-evolving market.
            </p>
          </div>
          
          <Card className="border-2 border-gray-200 shadow-lg hover:shadow-xl transition-shadow duration-300 rounded-2xl bg-white">
            <CardContent className="p-8 md:p-10">
              <form onSubmit={handleSubmit} className="space-y-5 md:space-y-6">
                <div className="grid md:grid-cols-2 gap-5 md:gap-6">
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-[#1E3A5F]">Name *</label>
                    <Input 
                      placeholder="Your full name"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="border border-gray-300 focus:border-[#FF6B35] text-[#1E3A5F] rounded-lg"
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-[#1E3A5F]">Email *</label>
                    <Input 
                      type="email"
                      placeholder="your.email@example.com"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="border border-gray-300 focus:border-[#FF6B35] text-[#1E3A5F] rounded-lg"
                      required
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-[#1E3A5F]">Phone (Optional)</label>
                  <Input 
                    type="tel"
                    placeholder="+1 (555) 000-0000"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="border border-gray-300 focus:border-[#FF6B35] text-[#1E3A5F] rounded-lg"
                  />
                </div>
                
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-[#1E3A5F]">Message *</label>
                  <Textarea 
                    placeholder="Tell us about your requirements..."
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                    className="border border-gray-300 focus:border-[#FF6B35] text-[#1E3A5F] min-h-32 rounded-lg"
                    required
                  />
                </div>
                
                <Button 
                  type="submit"
                  disabled={loading}
                  size="lg"
                  className="w-full bg-gradient-to-r from-[#FF6B35] to-[#F7931E] hover:from-[#F7931E] hover:to-[#FF6B35] text-white px-8 md:px-10 py-5 md:py-6 text-base md:text-lg font-semibold rounded-xl transition-all duration-300 hover:scale-105 shadow-xl hover:shadow-2xl"
                >
                  {loading ? 'Sending...' : 'Send Message'}
                  {!loading && <ArrowRight className="ml-2 h-5 w-5" />}
                </Button>
              </form>
              
              <div className="mt-6 text-center bg-[#FAFBFC] rounded-xl p-4 border border-gray-200">
                <p className="text-[#5B6B7F] text-sm">
                  Or email us directly at{' '}
                  <a href="mailto:info@coinexus.vip" className="text-[#FF6B35] hover:text-[#F7931E] font-semibold transition-colors">
                    info@coinexus.vip
                  </a>
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

    </div>
  );
};

export default Home;